import { isQuestionScored, type AnswerValue, type Question, type YesNoNa } from '../../audit/template';

/**
 * Input control for a single (non-sample-group) question. Mobile-first:
 * yes/no/na are large tap targets, not tiny radios.
 */
export function QuestionInput({
  question,
  value,
  onChange,
  inputId,
  labelId,
  describedBy,
}: {
  question: Question;
  value: AnswerValue | undefined;
  onChange: (value: AnswerValue | undefined) => void;
  /** id for the single input (anchors, tests). */
  inputId?: string;
  /** id of the question text — names every input and button group. */
  labelId?: string;
  /** id of the question's "More detail", read after the name by screen readers. */
  describedBy?: string;
}) {
  switch (question.type) {
    case 'yes_no':
    case 'yes_no_na':
      return (
        <YesNoNaInput
          value={value as YesNoNa | undefined}
          allowNa={question.type === 'yes_no_na'}
          neutral={!isQuestionScored(question)}
          onChange={onChange}
          labelledBy={labelId}
          describedBy={describedBy}
        />
      );
    case 'short_text':
      return (
        <input
          id={inputId}
          aria-labelledby={labelId}
          aria-describedby={describedBy}
          className="q-input"
          value={(value as string | undefined) ?? ''}
          onChange={(e) => onChange(e.target.value || undefined)}
        />
      );
    case 'long_text':
      return (
        <textarea
          id={inputId}
          aria-labelledby={labelId}
          aria-describedby={describedBy}
          className="q-input"
          rows={3}
          value={(value as string | undefined) ?? ''}
          onChange={(e) => onChange(e.target.value || undefined)}
        />
      );
    case 'number':
    case 'percent':
      return (
        <span className="q-number">
          <input
            id={inputId}
            aria-labelledby={labelId}
            aria-describedby={describedBy}
            className="q-input q-input-number"
            type="number"
            inputMode="decimal"
            min={question.type === 'percent' ? 0 : undefined}
            max={question.type === 'percent' ? 100 : undefined}
            value={(value as number | undefined) ?? ''}
            onChange={(e) =>
              onChange(e.target.value === '' ? undefined : Number(e.target.value))
            }
          />
          {question.type === 'percent' && <span className="muted">%</span>}
        </span>
      );
    case 'date':
      return (
        <input
          id={inputId}
          aria-labelledby={labelId}
          aria-describedby={describedBy}
          className="q-input q-input-date"
          type="date"
          value={(value as string | undefined) ?? ''}
          onChange={(e) => onChange(e.target.value || undefined)}
        />
      );
    case 'multi_select': {
      const selected = (value as string[] | undefined) ?? [];
      return (
        <div className="q-options" role="group" aria-labelledby={labelId} aria-describedby={describedBy}>
          {(question.options ?? []).map((opt) => (
            <label key={opt} className="q-check">
              <input
                type="checkbox"
                checked={selected.includes(opt)}
                onChange={(e) => {
                  const next = e.target.checked
                    ? [...selected, opt]
                    : selected.filter((o) => o !== opt);
                  onChange(next.length ? next : undefined);
                }}
              />
              {opt}
            </label>
          ))}
        </div>
      );
    }
    case 'sample_group':
      // Rendered by SampleGroupInput; never reaches here.
      return null;
  }
}

export function YesNoNaInput({
  value,
  allowNa,
  neutral = false,
  onChange,
  labelledBy,
  describedBy,
}: {
  value: YesNoNa | undefined;
  allowNa: boolean;
  /** Doesn't count toward the score: "No" is a fact, not a fail — no red (UI review 3, P4). */
  neutral?: boolean;
  onChange: (value: YesNoNa | undefined) => void;
  /** id of the text that names this group (the question / field label). */
  labelledBy?: string | undefined;
  /** The question's "More detail" (aria-describedby). */
  describedBy?: string | undefined;
}) {
  const options: Array<{ key: YesNoNa; label: string }> = [
    { key: 'yes', label: 'Yes' },
    { key: 'no', label: 'No' },
    ...(allowNa ? [{ key: 'na' as YesNoNa, label: 'N/A' }] : []),
  ];
  return (
    <div className="yn-group" role="radiogroup" aria-labelledby={labelledBy} aria-describedby={describedBy}>
      {options.map((opt) => (
        <button
          key={opt.key}
          type="button"
          role="radio"
          aria-checked={value === opt.key}
          className={
            value === opt.key ? `yn yn-on yn-${opt.key}${neutral ? ' yn-neutral' : ''}` : 'yn'
          }
          onClick={() => onChange(value === opt.key ? undefined : opt.key)}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}
